import { useMemo } from 'react';
import { useSliderState, SliderState } from '@react-stately/slider';
import _uniq from 'lodash/uniq';

import { getStepValue } from '.';

interface UseSliderStepsInput {
    /**
     * The default value for the `useSliderState` hook from @react-stately/slider. `useSliderState`
     * supports multiple values for a single slider in order to represent a range with a slider, so
     * the defaultValue will be formatted here as a number[]. This hook only supports a single
     * value, however, so the number[] will only contain one element.
     */
    defaultValue: number[];
    /**
     * A label for the slider
     */
    label?: string;
    /**
     * An Intl.NumberFormat instance for formatting the value
     */
    numberFormatter: Intl.NumberFormat;
    /**
     * Method to set the state of the value and invoke the onChange passed to
     * SliderWithDiscreteSteps
     */
    onChange: (valueList: number[]) => void;
    /**
     * A discrete list of steps that the user will be allowed to select from.
     */
    steps: number[];
    /**
     * The rounding type to use when a user enters a number that is not one of the listed `steps`.
     * See {@link https://github.com/trqk/trqk-client/blob/develop/packages/components/src/Slider/utils/getStepValue.ts | getStepValue}
     */
    roundType: SliderStepRoundType;
    /**
     * Current state of the value
     */
    value: number[];
}

export const useSliderStateWithDiscreteSteps = (
    input: UseSliderStepsInput,
): SliderState => {
    const { defaultValue, label, numberFormatter, roundType, value } = input;

    // Sort and deduplicate steps, then calculate the minStepSize, i.e. smallest difference between
    // two steps. The minStepSize is used to set the `step` input for `useSliderState` so that
    // `useSliderState` does not skip over any steps in the `input.steps` list.
    const { steps, minStepSize } = useMemo((): {
        steps: number[];
        minStepSize: number;
    } => {
        const sorted = [...input.steps].sort((a: number, b: number): number => {
            if (a > b) {
                return 1;
            }

            if (a < b) {
                return -1;
            }

            return 0;
        });

        const stepsResult = _uniq(sorted);

        const minStepSizeResult = stepsResult.reduce(
            (acc: number, curr: number, currIndex: number): number => {
                if (currIndex === 0) {
                    return acc;
                }

                const prior = stepsResult[currIndex - 1];

                const diff = curr - prior;

                if (diff < acc) {
                    return diff;
                }

                return acc;
            },
            1,
        );

        return {
            steps: stepsResult,
            minStepSize: minStepSizeResult,
        };
    }, [input.steps]);

    const maxValue = steps[steps.length - 1] ?? 100;
    const minValue = steps[0] ?? 0;

    const state = useSliderState({
        defaultValue,
        label,
        numberFormatter,
        maxValue,
        minValue,
        value,
        step: minStepSize,
        onChange: (valueList: number[]): void => {
            const incomingValue = valueList[0];

            if (incomingValue !== undefined) {
                const stepValue = getStepValue(incomingValue, steps, roundType);

                if (value[0] !== stepValue) {
                    input.onChange([stepValue]);
                }
            }
        },
    });

    return state;
};
